import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TabsHeader from "../components/Tabs/TabsHeader";
import { isNumber } from "lodash";

const EMPTY_PROPS = {};
const DEFAULT_OPTIONS = { header: true };

export default (_tabs = [], props = EMPTY_PROPS, options = DEFAULT_OPTIONS) => {
  const externalIndex = options?.activeTabIndex;
  const [focusedTabIndex, setFocusedTabIndex] = useState(
    isNumber(externalIndex) ? externalIndex : 0
  );
  const lastExternalIndex = useRef(externalIndex);

  useEffect(() => {
    if (!isNumber(externalIndex)) return;
    if (lastExternalIndex.current === externalIndex) return;
    lastExternalIndex.current = externalIndex;
    setFocusedTabIndex(externalIndex);
  }, [externalIndex]);

  const tabs = useMemo(
    () =>
      _tabs.filter(
        (tab) => !tab?.shouldRenderPropsKey || props[tab.shouldRenderPropsKey]
      ),
    [props, _tabs]
  );

  const onTabChange = options?.onTabChange;
  const setActiveIndex = useCallback((index) => {
    setFocusedTabIndex(index);
    onTabChange?.(index);
  }, [onTabChange]);

  const header = useMemo(
    () =>
      options?.header !== false && (
        <TabsHeader
          tabs={tabs}
          activeIndex={focusedTabIndex}
          setActiveIndex={setActiveIndex}
        />
      ),
    [tabs, focusedTabIndex, options?.header, setActiveIndex]
  );

  const next = useCallback(() => {
    setFocusedTabIndex((index) => (index + 1) % Math.max(tabs.length, 1));
  }, [tabs.length]);

  const content = useMemo(() => {
    const { Component } = tabs[focusedTabIndex] ?? {};
    return Component ? <Component next={next} {...props} /> : null;
  }, [focusedTabIndex, tabs, props, next]);

  return {
    header,
    content,
    next,
    focusedTabIndex,
    setFocusedTabIndex,
  };
};
